import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import type { GenerationAsset } from '../state/useGenerationState';

/**
 * 商品AIプロファイルによる属性推奨 (計画書 §5.1)。
 * GET /generation/recommendations の subject_type。
 * 生成画面の選択素材は必ず material / temporary_material のいずれか
 * (product / amazon_product への直接紐付き選択 UI はまだ存在しない)。
 */
export type RecommendationSubjectType = 'product' | 'amazon_product' | 'material' | 'temporary_material';

export type RecommendationSubject = {
  subject_type: RecommendationSubjectType;
  subject_id: string;
};

export type RecommendationEntry = {
  key: string;
  value: string;
  score?: number;
};

export type RecommendationPerson = {
  gender?: string;
  age?: number;
};

/** tone / visual_style は scenario_attribute_nodes の option 解決結果 (計画書§5.4/§5.5)。
 * マッチ無し (スコア0) / reco_tags 未シード等は null。 */
export type RecommendationFieldEntry = {
  value: string;
  label: string;
  score: number;
};

export type ReadyRecommendations = {
  status: 'ready';
  strong: RecommendationEntry[];
  suggested: RecommendationEntry[];
  person: RecommendationPerson;
  target?: Record<string, unknown>;
  confidence?: number;
  tone?: RecommendationFieldEntry | null;
  visual_style?: RecommendationFieldEntry | null;
  /** 「モーショングラフィックス」の全編 (whole) の推奨 (映像スタイルの最高スコアを上回るときだけ・計画 md §10.14)。 */
  motion_graphics?: RecommendationFieldEntry | null;
  /** 想定尺 (scenario_attribute_nodes(duration) の option 解決結果。計画書§5.4/§8 P4)。
   * tone_tags ∪ style_tags と照合する。マッチ無し等は null。 */
  duration?: RecommendationFieldEntry | null;
};

export type RecommendationsResponse =
  | ReadyRecommendations
  | { status: 'pending' }
  | { status: 'failed' }
  | { status: 'unsupported' }
  // サーバは DB 障害等で推奨を出せないとき {status:'none'} を返す (補助機能なので
  // 500 にせずランダムへ劣化させる)。フロントは 'none' をランダム扱いにする。
  | { status: 'none' };

/**
 * 選択中素材の先頭1件から recommendations API の subject を導出する。
 * `GenerationAsset.type` (`'temporary' | 'cloud'`) が temporary_materials /
 * materials の区別と一致する (id はそれぞれのテーブルの主キー。
 * useProductMaterials.ts / useProductImageSelection.ts の addAsset 呼び出し参照)。
 */
export function resolveRecommendationSubject(
  selectedAssets: Pick<GenerationAsset, 'id' | 'type'>[],
): RecommendationSubject | null {
  const asset = selectedAssets[0];
  if (!asset) return null;
  return {
    subject_type: asset.type === 'temporary' ? 'temporary_material' : 'material',
    subject_id: asset.id,
  };
}

/** pending 状態でポーリングする間隔・最大取得回数 (計画書§5.3 のボタン状態遷移用)。 */
const PENDING_REFETCH_INTERVAL_MS = 5_000;
const PENDING_REFETCH_MAX_FETCHES = 6;
const PENDING_FALLBACK_AFTER_MS = PENDING_REFETCH_INTERVAL_MS * PENDING_REFETCH_MAX_FETCHES;

/**
 * 商品AIプロファイル推奨を取得するフック。
 *
 * プロファイルは事前計算済みで頻繁に変わらないため staleTime は長め。
 * status:'pending' の間だけ 5 秒間隔で最大 6 回程度 refetch し、
 * ready/failed/unsupported になったら自動的に止まる。
 */
export function useRecommendations(subject: RecommendationSubject | null) {
  const [isPendingExpired, setIsPendingExpired] = useState(false);
  const subjectType = subject?.subject_type;
  const subjectId = subject?.subject_id;

  const query = useQuery<RecommendationsResponse>({
    queryKey: ['generation', 'recommendations', subject?.subject_type, subject?.subject_id],
    queryFn: async () => {
      const { data } = await apiClient.get<RecommendationsResponse>('/generation/recommendations', {
        params: {
          subject_type: subject!.subject_type,
          subject_id: subject!.subject_id,
        },
      });
      return data;
    },
    enabled: !!subject,
    staleTime: 5 * 60_000,
    refetchInterval: (query) => {
      if (query.state.data?.status !== 'pending') return false;
      if (isPendingExpired) return false;
      if (query.state.dataUpdateCount >= PENDING_REFETCH_MAX_FETCHES) return false;
      return PENDING_REFETCH_INTERVAL_MS;
    },
  });

  useEffect(() => {
    setIsPendingExpired(false);
  }, [subjectType, subjectId]);

  useEffect(() => {
    if (query.data?.status !== 'pending' || isPendingExpired) return undefined;
    const timer = window.setTimeout(() => {
      setIsPendingExpired(true);
    }, PENDING_FALLBACK_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [query.data?.status, isPendingExpired, subjectType, subjectId]);

  return {
    ...query,
    isPendingExpired,
  };
}
